import type { ComponentChildren } from 'preact'
import { useEffect, useRef, useState } from 'preact/hooks'
import { finishTime, ordinal } from '../text'
import ui from '../ui.module.css'
import styles from './podium.module.css'
import type { TimeRow } from './podiumData'

/** A vertical span (top and bottom edges, px). */
interface Span {
  top: number
  bottom: number
}

/**
 * How far to scroll a box that shows `view` so `row` is wholly in it (0 when it already is): the
 * least scroll that brings the row's bottom up, never past its top.
 */
export function revealDelta(row: Span, view: Span): number {
  if (row.bottom > view.bottom) return Math.min(row.bottom - view.bottom, row.top - view.top)
  if (row.top < view.top) return row.top - view.top
  return 0
}

/** Scroll the nearest scrolling box around `el` so it is in view (no page scroll). */
function reveal(el: HTMLElement | null): void {
  if (!el || typeof el.getBoundingClientRect !== 'function') return
  let box = el.parentElement
  while (box && box.scrollHeight <= box.clientHeight) box = box.parentElement
  if (!box) return
  box.scrollTop += revealDelta(el.getBoundingClientRect(), box.getBoundingClientRect())
}

/**
 * Place, name, time or DNF for everyone, your row highlighted (the old results list). Your row
 * is scrolled into view when the list shows (QA: below the fold on a 740×360 phone); `shown`
 * changes when a toggle opens it.
 */
export function TimesList({ rows, shown = true }: { rows: readonly TimeRow[]; shown?: boolean }) {
  const mine = useRef<HTMLLIElement>(null)
  useEffect(() => reveal(mine.current), [shown])
  return (
    <ol class={styles.times} aria-label="Times">
      {rows.map((r) => (
        <li
          key={r.index}
          ref={r.isMe ? mine : null}
          class={`${styles.time} ${r.isMe ? styles.me : ''}`}
        >
          <span class={styles.place}>{ordinal(r.placement)}</span>
          <span class={styles.name}>{r.name}</span>
          <span class={styles.clock}>{r.finished ? finishTime(r.finishMs) : 'DNF'}</span>
        </li>
      ))}
    </ol>
  )
}

interface Props {
  times: readonly TimeRow[]
  /** The reward rows and Phase 2's lines. */
  children: ComponentChildren
  /** Race again / Rematch / Hub. */
  actions: ComponentChildren
}

/**
 * The results' DOM over the Pixi podium (PHASE3 T3.5): a panel on the right third (the bottom
 * half in portrait) titled **Match result**, its body scrolling inside when it does not fit,
 * the buttons always visible at its foot. The Times list sits under the rows on narrow screens
 * (< 900 px) and behind a **Times** toggle on wide ones. The rest of the screen lets taps
 * through to the podium's name tags.
 */
export function PodiumFrame({ times, children, actions }: Props) {
  const [open, setOpen] = useState(false)
  return (
    <div class={styles.podium}>
      <aside class={styles.panel} aria-label="Match result">
        <div class={styles.head}>
          <h2 class={styles.title}>Match result</h2>
          <button
            type="button"
            class={`${ui.button} ${ui.small} ${styles.timesToggle}`}
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
          >
            Times
          </button>
        </div>
        <div class={styles.body}>
          {children}
          <div class={`${styles.timesBox} ${open ? styles.open : ''}`}>
            <TimesList rows={times} shown={open} />
          </div>
        </div>
        <div class={styles.foot}>{actions}</div>
      </aside>
    </div>
  )
}
